import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  FormElementAnalyticsDocument,
  FormInsightsOverviewElementFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { ChartWidget } from 'modules/engagement_surveys/components/FormInsightsOverview/InsightsVisualization/ChartWidget'
import type { FormFiltersStorage } from 'modules/forms'

interface Props {
  element: FormInsightsOverviewElementFragment
  activeFilters?: FormFiltersStorage['filters']
  cycleIds?: number[]
}

export function Number({ element, activeFilters, cycleIds }: Props) {
  const variablesKey = JSON.stringify(activeFilters)
  const cycleIdsKey = cycleIds?.join(',') ?? ''

  const source = useGraphqlDataCollectionSource(
    {
      document: FormElementAnalyticsDocument,
      mapData: (data) => {
        const {
          series,
          meta: { total },
          categories,
        } = data.forms.elementAnalyticsConnection.nodes?.[0] ?? {
          series: [],
          meta: { total: 0 },
          categories: [],
        }

        return series.map((item, index) => ({
          label: categories?.[index] ?? '',
          value: item,
          ratio: Math.round((item / total) * 100),
        }))
      },
      mapVariables: () => ({
        elementId: element.id,
        ...activeFilters,
        cycleIds,
      }),
      fetchPolicy: 'cache-and-network',
      mapPagination: () => {
        return {
          total: 0,
        }
      },
    },
    [variablesKey, cycleIdsKey]
  )

  return (
    <ChartWidget
      title={element.label}
      description={element.description || ''}
      questionType={element.type}
    >
      <Box padding="s8">
        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('forms.analytics.numbers_table.label'),
                    render: (item: { label: string; value: number; ratio: number }) => item.label,
                  },
                  {
                    label: i18n.t('forms.analytics.numbers_table.value'),
                    render: (item: { label: string; value: number; ratio: number }) => ({
                      type: 'number',
                      value: item.value,
                    }),
                  },
                  {
                    label: i18n.t('forms.analytics.numbers_table.ratio'),
                    render: (item: { label: string; value: number; ratio: number }) =>
                      `${item.ratio}%`,
                  },
                ],
              },
            },
          ]}
        />
      </Box>
    </ChartWidget>
  )
}
